import { lazy, Suspense, useEffect, useRef } from "react";
import { bool, func, string } from "prop-types";
import { matchPath, Route, Router, Switch } from "react-router-dom";
import Media from "react-media";
import { Helmet } from "react-helmet";
import * as Sentry from "@sentry/react";

import {
  BREAKPOINT_WRAPPER,
  HOST_ENV,
  RESTRICT_LISTINGS,
  routes,
  SENTRY_DSN,
  SITE_VERSION,
  URL_API_DOMAIN,
} from "../../constants";
import history from "../../helpers/history";

import { AppRoute, AppShortLink } from "./components";
import Page from "../page";

if (process.env.NODE_ENV !== "development" && !!SENTRY_DSN) {
  Sentry.init({
    dsn: SENTRY_DSN,
    environment: HOST_ENV,
    release: `holt.used.public-${SITE_VERSION}`,
    integrations: [new Sentry.BrowserTracing()], // TO-DO: integrate w/ react-router after v6 upgrade
    tracesSampleRate: 0.1,
    beforeSend(event, hint) {
      const { name } = hint?.originalException || {};

      // Don't send these errors to Sentry
      if (/AbortError|ChunkLoadError/gi.test(name)) {
        return null;
      }

      return event;
    },
    // Not the most elegant solution, but think this error is thrown
    // by sentry/tracing and it's consuming our Sentry error quota
    ignoreErrors: ["Cannot read properties of undefined (reading 'prototype')"],
    // SENTRY REPLAY DISABLED: now using HotJar loaded via Google Tag Manager
    // Set replay sample rate
    // Only enabled when using custom useReplay hook
    // replaysSessionSampleRate: 0.05, // 5% of sessions
    // replaysOnErrorSampleRate: 0, // disabled for errors
  });
}

const Alerts = lazy(() => import("../alerts"));
const Auth = lazy(() => import("../auth"));
const Blog = lazy(() => import("../blog"));
const BlogIndex = lazy(() => import("../blog-index"));
const Buy = lazy(() => import("../buy"));
const Catalog = lazy(() => import("../catalog"));
const CampaignPage = lazy(() => import("../campaign-page"));
const Compare = lazy(() => import("../compare"));
const Consignment = lazy(() => import("../consignment"));
const Contact = lazy(() => import("../contact"));
const Disclosures = lazy(() => import("../disclosures"));
const Download = lazy(() => import("../download"));
const EmailSignup = lazy(() => import("../email-signup"));
const Home = lazy(() => import("../home"));
const LandingPage = lazy(() => import("../landing-page"));
const Listing = lazy(() => import("../listing/Listing.container"));
const Location = lazy(() => import("../location"));
const NotFound = lazy(() => import("../not-found"));
const Partners = lazy(() => import("../partners"));
const Prequalify = lazy(() => import("../prequalify"));
const PrismicPreview = lazy(() => import("../prismic/PrismicPreview"));
const Privacy = lazy(() => import("../privacy"));
const Results = lazy(() => import("../results"));
const ServiceHistory = lazy(() => import("../service-history"));
const Specials = lazy(() => import("../specials"));
const Terms = lazy(() => import("../terms"));
const Watch = lazy(() => import("../watch"));
const WatchEmailConfirm = lazy(() => import("../watch-email-confirm"));
const Why = lazy(() => import("../why"));

/**
 * Wraps the application with React Router,
 * connects React Router to the shared browser history,
 * and adds an error boundary for routes within the application
 * * Each route returns a component or redirect
 * * Public routes use React Routers default AppRoute component
 * * Each page module uses React.lazy for code splitting
 * @param {object} props
 */
const App = props => {
  const { checkAuth, routeChange } = props;
  props.checkFeatureSupport();

  const pathName = useRef(window?.location.pathname);

  useEffect(() => {
    checkAuth();
    const unlisten = history.listen((location, action) => {
      if (
        !matchPath(location.pathname, {
          path: [routes.forgot.path, routes.login.path, routes.reset.path],
        })
      ) {
        checkAuth();
      }

      if (pathName.current !== location.pathname) {
        routeChange();
      }

      pathName.current = location.pathname;
    });
    return unlisten;
  }, [checkAuth, routeChange]);

  return (
    <Router history={history}>
      <Helmet defer={false}>
        <link rel="preconnect" href={URL_API_DOMAIN} crossorigin />
        <link
          href="https://fonts.googleapis.com/css?family=Oswald:400,600,700&display=swap"
          rel="stylesheet"
        />
        <meta
          name="viewport"
          content="width=device-width, height=device-height, initial-scale=1, shrink-to-fit=no"
        />
      </Helmet>
      <Media
        onChange={matches => {
          const device = matches ? "desktop" : "mobile";

          if (!props.targetDevice || device !== props.targetDevice) {
            props.updateTargetDevice(device);
          }
        }}
        query={BREAKPOINT_WRAPPER}
      />
      <Suspense fallback={null}>
        <Switch>
          <Route
            {...props}
            exact
            path={[
              routes.forgot.path,
              routes.login.path,
              `${routes.reset.path}/:token`,
            ]}
            component={Auth}
          />
          <Route
            render={() => (
              <Page
                trackEvent={props.trackEvent}
                trackOutboundLink={props.trackOutboundLink}
                trackModalView={props.trackModalView}
              >
                <Switch>
                  <AppRoute
                    {...props}
                    exact
                    path={routes.home.path}
                    component={Home}
                  />
                  <AppRoute
                    {...props}
                    exact
                    path={`${routes.shortLink.path}/:hash/:action(confirm|unwatch)/`}
                    component={WatchEmailConfirm}
                    restricted={RESTRICT_LISTINGS}
                  />
                  <AppShortLink
                    {...props}
                    exact
                    path={`${routes.shortLink.path}/:hash/:action/`}
                  />
                  <AppRoute
                    {...props}
                    exact
                    path={routes.results.path}
                    component={Results}
                    restricted={RESTRICT_LISTINGS}
                  />
                  <AppRoute
                    {...props}
                    exact
                    path={`${routes.alerts.path}/:id?`}
                    component={Alerts}
                    restricted={!props.authenticated}
                  />
                  <AppRoute
                    {...props}
                    exact
                    path={[
                      `${routes.blog.path}`,
                      `${routes.blogCategory.path}`,
                      `${routes.blogTag.path}`,
                      `${routes.blogCategory.path}/:name`,
                      `${routes.blogTag.path}/:name`,
                    ]}
                    component={BlogIndex}
                  />
                  <AppRoute
                    {...props}
                    exact
                    path={`${routes.blog.path}/:uid`}
                    component={Blog}
                  />
                  <AppRoute
                    {...props}
                    exact
                    path={routes.preview.path}
                    component={PrismicPreview}
                    restricted={true}
                    restrictedToRoles={["admin"]}
                  />
                  <AppRoute
                    {...props}
                    exact
                    path={`${routes.buy.path}/:slug`}
                    component={Buy}
                    restricted={RESTRICT_LISTINGS}
                  />
                  <AppRoute
                    {...props}
                    exact
                    path={routes.compile.path}
                    component={Catalog}
                    restricted={true}
                  />
                  <AppRoute
                    {...props}
                    exact
                    path={routes.compare.path}
                    component={Compare}
                    restricted={RESTRICT_LISTINGS}
                  />
                  <AppRoute
                    {...props}
                    exact
                    path={routes.consignment.path}
                    component={Consignment}
                  />
                  <AppRoute
                    {...props}
                    exact
                    path={routes.contact.path}
                    component={Contact}
                  />
                  <AppRoute
                    {...props}
                    exact
                    path={routes.disclosures.path}
                    component={Disclosures}
                  />
                  <AppRoute
                    {...props}
                    exact
                    path={[
                      `${routes.download.path}/:id`,
                      `${routes.download.path}/weekly/:id`,
                    ]}
                    component={Download}
                  />
                  <AppRoute
                    {...props}
                    exact
                    path={routes.emailSignup.path}
                    component={EmailSignup}
                  />
                  <AppRoute
                    {...props}
                    exact
                    path={`${routes.landingPage.path}/:uid`}
                    component={LandingPage}
                  />
                  <AppRoute
                    {...props}
                    exact
                    path={`${routes.location.path}/:slug`}
                    component={Location}
                  />
                  <AppRoute
                    {...props}
                    exact
                    path={routes.notFound.path}
                    component={NotFound}
                  />
                  <AppRoute
                    {...props}
                    exact
                    path={routes.partners.path}
                    component={Partners}
                  />
                  <AppRoute
                    {...props}
                    exact
                    path={`${routes.prequalify.path}/:slug`}
                    component={Prequalify}
                    restricted={RESTRICT_LISTINGS}
                  />
                  <AppRoute
                    {...props}
                    exact
                    path={routes.privacy.path}
                    component={Privacy}
                  />
                  <AppRoute
                    {...props}
                    exact
                    path={`${routes.serviceHistory.path}/:slug`}
                    component={ServiceHistory}
                    restricted={true}
                  />
                  <AppRoute
                    {...props}
                    exact
                    path={routes.specials.path}
                    component={Specials}
                    restricted={RESTRICT_LISTINGS}
                  />
                  <AppRoute
                    {...props}
                    exact
                    path={routes.terms.path}
                    component={Terms}
                  />
                  <AppRoute
                    {...props}
                    exact
                    path={routes.watch.path}
                    component={Watch}
                    restricted={true}
                  />
                  <AppRoute
                    {...props}
                    exact
                    path={routes.why.path}
                    component={Why}
                  />
                  <AppRoute
                    {...props}
                    path={routes.campaignPage.path}
                    component={CampaignPage}
                    restricted={RESTRICT_LISTINGS}
                  />
                  {/* Restricted w/in Listing component */}
                  <AppRoute {...props} path="/:slug?" component={Listing} />
                </Switch>
              </Page>
            )}
          />
        </Switch>
      </Suspense>
    </Router>
  );
};

App.propTypes = {
  authenticated: bool,
  checkAuth: func.isRequired,
  checkFeatureSupport: func.isRequired,
  targetDevice: string,
  trackEvent: func,
  trackOutboundLink: func,
  updateTargetDevice: func.isRequired,
  userRole: string,
};

App.defaultProps = {
  authenticated: false,
};

export default App;
